import {usePrefetchAssetsHistory} from '@domain/assets'
import {usePrefetchPeakLoadWindows} from '@domain/electricity'
import {usePrefetchMaterialStorageHistory} from '@domain/material-storage'
import {usePrefetchMaterialsRecipes} from '@domain/materials'
import {useUpmPlantContext} from '@hconnect/upm'
import {Stack} from '@mui/material'
import {
  PlanningChartScheduleDataProvider,
  PlanningChartStartEndProvider,
  SelectedScheduleItemProvider
} from '@widgets/planning-schedule-chart'
import React from 'react'

import {usePeriodicAutofillCheck, useSelectedTimeframe} from '../hooks'
import type {MomentRange} from '../types'

import {DailyProductionPlanningActions} from './dailyPlanning'
import {PageProductionPlanningHeader} from './PageProductionPlanningHeader'
import {ProductionPlanningChartContainer} from './ProductionPlanningChartContainer'

export const MIN_RANGE_HOURS = 12
export const MAX_RANGE_HOURS = 24 * 7
interface LayoutDailyPlanningProps {
  updateSliderDefaultRange: (range: number) => void
  sliderDefaultRange: number
  boundary: MomentRange
}

export const LayoutDailyPlanning: React.FC<LayoutDailyPlanningProps> = ({
  updateSliderDefaultRange,
  sliderDefaultRange,
  boundary
}) => {
  const {plantId: plantCode} = useUpmPlantContext()
  const {selectedTimeframe} = useSelectedTimeframe()
  if (!selectedTimeframe) {
    throw new Error('selectedTimeframe is not defined')
  }

  usePrefetchPeakLoadWindows(plantCode, selectedTimeframe)
  usePeriodicAutofillCheck()

  const [startOfPlan, endOfPlan] = selectedTimeframe

  // prefetching required data
  usePrefetchMaterialStorageHistory(plantCode, startOfPlan.toISOString(), endOfPlan.toISOString())
  usePrefetchMaterialsRecipes(plantCode)
  usePrefetchAssetsHistory(plantCode, selectedTimeframe)

  return (
    <PlanningChartStartEndProvider
      sliderDefaultRange={sliderDefaultRange}
      sliderMinMaxRange={[MIN_RANGE_HOURS, MAX_RANGE_HOURS]}
      boundary={boundary}
      updateSliderDefaultRange={updateSliderDefaultRange}
      startOfPlan={startOfPlan}
      endOfPlan={endOfPlan}
    >
      <SelectedScheduleItemProvider>
        <PlanningChartScheduleDataProvider>
          <Stack spacing={2}>
            <PageProductionPlanningHeader
              actions={<DailyProductionPlanningActions />}
              plantCode={plantCode}
            />
            <ProductionPlanningChartContainer plantCode={plantCode} />
          </Stack>
        </PlanningChartScheduleDataProvider>
      </SelectedScheduleItemProvider>
    </PlanningChartStartEndProvider>
  )
}
